import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, HardHat, X } from "@phosphor-icons/react";
import BrandMark from "../components/BrandMark";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const WAREHOUSE_BG =
  "https://images.unsplash.com/photo-1631856956423-2b95dae0ba74?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBpbmR1c3RyaWFsJTIwc2hlbHZlc3xlbnwwfHx8fDE3Nzc5MzM3MTZ8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const navigate = useNavigate();

  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Authenticated");
      navigate("/dashboard");
    } else {
      toast.error(res.error);
    }
  };

  const fillDemo = (em, pw) => {
    setEmail(em);
    setPassword(pw);
  };

  const onForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotSubmitting(true);
    try {
      await api.post("/auth/password-reset/request", { email: forgotEmail });
      toast.success("If that email is registered, a reset link is on its way.");
      setForgotOpen(false);
      setForgotEmail("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send reset email.");
    } finally {
      setForgotSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual */}
      <div className="relative hidden lg:flex bg-[#1A2332] text-zinc-50 overflow-hidden">
        <img
          src={WAREHOUSE_BG}
          alt="Warehouse"
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950/95 via-zinc-950/70 to-zinc-950/95" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <BrandMark size={56} className="flex-shrink-0 rounded-lg" />
            <div>
              <div className="font-display text-2xl font-bold leading-none">ezItemizer</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mt-1">
                Inventory Control System
              </div>
            </div>
          </div>

          <div className="space-y-6 max-w-lg">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF9800]">
              Trailer Manufacturing · v2.0
            </div>
            <h1 className="font-display text-5xl font-black leading-[0.95] tracking-tight">
              Every fender.<br />Every axle.<br />Tracked.
            </h1>
            <p className="text-zinc-400 leading-relaxed text-base max-w-md">
              Industrial-grade parts tracking built for the floor. Real stock counts, role-aware access, low-stock alerts, and audited movement history.
            </p>
            <div className="grid grid-cols-3 gap-px bg-zinc-800 mt-8">
              {[
                { label: "Parts SKUs", value: "Unlimited" },
                { label: "Roles", value: "3 Tiers" },
                { label: "Audit Log", value: "Full" },
              ].map((s) => (
                <div key={s.label} className="bg-[#1A2332] px-4 py-4">
                  <div className="font-mono text-xl font-bold">{s.value}</div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-1">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            <span className="h-1.5 w-1.5 bg-emerald-500" />
            All systems operational
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-zinc-50">
        <div className="w-full max-w-md" data-testid="login-card">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <BrandMark size={48} className="flex-shrink-0 rounded-lg" />
            <div>
              <div className="font-display text-xl font-bold">ezItemizer</div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                Inventory Control
              </div>
            </div>
          </div>

          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF9800] mb-2">
            Operator Access
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">
            Sign in to continue
          </h2>
          <p className="text-sm text-zinc-500 mt-2">
            Use your assigned credentials. Contact an admin for access.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-700">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="operator@trailer.com"
                data-testid="login-email-input"
                className="mt-2 w-full px-4 py-3 bg-white border border-zinc-300 focus:border-[#FF9800] focus:outline-none focus:ring-1 focus:ring-[#FF9800] font-mono text-sm"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-700">
                Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
                className="mt-2 w-full px-4 py-3 bg-white border border-zinc-300 focus:border-[#FF9800] focus:outline-none focus:ring-1 focus:ring-[#FF9800] font-mono text-sm"
              />
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={() => { setForgotEmail(email); setForgotOpen(true); }}
                  data-testid="forgot-password-link"
                  className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-[#FF9800]"
                >
                  Forgot password?
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-button"
              className="w-full bg-[#FF9800] hover:bg-[#E68900] text-[#1A2332] font-bold py-3 px-4 flex items-center justify-center gap-2 transition-colors duration-200 active:scale-[0.99] disabled:opacity-60"
            >
              {submitting ? "Authenticating…" : "Sign in"}
              <ArrowRight size={16} weight="bold" />
            </button>
          </form>

          <div className="mt-10 border-t border-zinc-300 pt-6">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-3">
              Demo accounts · Click to fill
            </div>
            <div className="space-y-1.5">
              {[
                { label: "Admin", email: "admin@trailer.com", pw: "Admin@123", color: "bg-[#1A2332] text-white" },
                { label: "Warehouse", email: "staff@trailer.com", pw: "Staff@123", color: "bg-amber-400 text-amber-950" },
                { label: "Viewer", email: "viewer@trailer.com", pw: "Viewer@123", color: "bg-zinc-200 text-zinc-700" },
              ].map((d) => (
                <button
                  key={d.email}
                  type="button"
                  onClick={() => fillDemo(d.email, d.pw)}
                  data-testid={`demo-fill-${d.label.toLowerCase()}`}
                  className="w-full flex items-center justify-between px-3 py-2 border border-zinc-300 hover:border-[#FF9800] hover:bg-white transition-colors group"
                >
                  <span className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${d.color}`}>
                      {d.label}
                    </span>
                    <span className="font-mono text-xs text-zinc-700">{d.email}</span>
                  </span>
                  <HardHat size={14} weight="duotone" className="text-zinc-400 group-hover:text-[#FF9800]" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Forgot password modal */}
      {forgotOpen && (
        <div
          className="fixed inset-0 z-50 bg-zinc-950/80 flex items-center justify-center px-4"
          onClick={() => !forgotSubmitting && setForgotOpen(false)}
          data-testid="forgot-password-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white border border-zinc-300 w-full max-w-md"
          >
            <div className="bg-[#1A2332] text-white px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BrandMark size={24} className="rounded" />
                <span className="font-display font-bold text-sm">Reset password</span>
              </div>
              <button
                type="button"
                onClick={() => setForgotOpen(false)}
                disabled={forgotSubmitting}
                className="p-1 hover:bg-[#243044]"
                data-testid="forgot-modal-close"
              >
                <X size={14} weight="bold" />
              </button>
            </div>
            <form onSubmit={onForgotSubmit} className="p-5 space-y-4">
              <p className="text-sm text-zinc-600">
                Enter your account email and we'll send a reset link if it exists.
              </p>
              <input
                type="email"
                required
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="forgot-email-input"
                className="w-full px-3 py-2.5 bg-white border border-zinc-300 focus:border-[#FF9800] focus:outline-none focus:ring-1 focus:ring-[#FF9800] font-mono text-sm"
                autoFocus
                disabled={forgotSubmitting}
              />
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setForgotOpen(false)}
                  disabled={forgotSubmitting}
                  className="px-4 py-2 text-xs font-bold uppercase tracking-widest text-zinc-700 hover:text-zinc-900"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={forgotSubmitting || !forgotEmail}
                  data-testid="forgot-submit-button"
                  className="bg-[#FF9800] hover:bg-[#E68900] text-[#1A2332] px-5 py-2.5 text-xs font-bold uppercase tracking-widest disabled:opacity-60"
                >
                  {forgotSubmitting ? "Sending…" : "Send reset link"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
